/**
 * WebRTC Service for Multi-Party Video Meetings
 * Implements peer connection management with ICE restart and reconnection
 */

class WebRTCService {
  constructor(socket, localStream) {
    this.socket = socket;
    this.localStream = localStream;
    this.peers = new Map(); // peerId -> RTCPeerConnection
    this.remoteTracks = new Map(); // peerId -> { audio, video, screen }
    this.iceServers = this.getDefaultIceServers();
    this.onRemoteStreamCallback = null;
    this.onRemoteTrackRemovedCallback = null;
    this.activeSpeakerDetection = true;
    this.audioLevels = new Map(); // peerId -> audio level
  }

  getDefaultIceServers() {
    return [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' },
      { urls: 'stun:stun2.l.google.com:19302' },
      // TURN servers can be configured via environment or API
      ...(this.getTurnServers())
    ];
  }

  getTurnServers() {
    // This will be populated from backend API with time-limited credentials
    const turnConfig = window.turnConfig || {};
    if (turnConfig.urls) {
      return [{
        urls: turnConfig.urls,
        username: turnConfig.username,
        credential: turnConfig.credential
      }];
    }
    return [];
  }

  setTurnConfig(config) {
    if (config && config.urls) {
      window.turnConfig = config;
      this.iceServers = this.getDefaultIceServers();
    }
  }

  onRemoteStream(callback) {
    this.onRemoteStreamCallback = callback;
  }

  onRemoteTrackRemoved(callback) {
    this.onRemoteTrackRemovedCallback = callback;
  }

  async createPeerConnection(peerId, isInitiator = false) {
    const config = {
      iceServers: this.iceServers,
      iceTransportPolicy: 'all', // Can be 'relay' to force TURN
      bundlePolicy: 'max-bundle',
      rtcpMuxPolicy: 'require',
      iceCandidatePoolSize: 10
    };

    const pc = new RTCPeerConnection(config);
    this.peers.set(peerId, pc);

    // Add local tracks to peer connection
    if (this.localStream) {
      this.localStream.getTracks().forEach(track => {
        pc.addTrack(track, this.localStream);
      });
    }

    // Enable simulcast for video tracks if supported
    const senders = pc.getSenders();
    senders.forEach(sender => {
      if (sender.track && sender.track.kind === 'video') {
        const params = sender.getParameters();
        if (!params.encodings) {
          params.encodings = [
            { rid: 'h', maxBitrate: 900000 },
            { rid: 'm', maxBitrate: 300000, scaleResolutionDownBy: 2 },
            { rid: 'l', maxBitrate: 100000, scaleResolutionDownBy: 4 }
          ];
          sender.setParameters(params).catch(err => {
            console.warn('Failed to set simulcast parameters:', err);
          });
        }
      }
    });

    // Handle ICE candidates
    pc.onicecandidate = (event) => {
      if (event.candidate) {
        this.socket.emit('webrtc-ice-candidate', {
          to: peerId,
          candidate: event.candidate
        });
      }
    };

    // Handle ICE connection state changes
    pc.oniceconnectionstatechange = () => {
      console.log(`ICE connection state for ${peerId}:`, pc.iceConnectionState);
      
      if (pc.iceConnectionState === 'failed') {
        console.log(`ICE failed for ${peerId}, attempting restart`);
        this.restartIce(peerId);
      } else if (pc.iceConnectionState === 'disconnected') {
        console.log(`ICE disconnected for ${peerId}, waiting for reconnect`);
        // Wait a bit before attempting restart
        setTimeout(() => {
          if (pc.iceConnectionState === 'disconnected') {
            this.restartIce(peerId);
          }
        }, 5000);
      }
    };

    // Handle incoming tracks
    pc.ontrack = (event) => {
      console.log(`Received track from ${peerId}:`, event.track.kind);
      
      if (!this.remoteTracks.has(peerId)) {
        this.remoteTracks.set(peerId, {});
      }
      
      const tracks = this.remoteTracks.get(peerId);
      
      if (event.track.kind === 'video') {
        tracks.video = event.track;
      } else if (event.track.kind === 'audio') {
        tracks.audio = event.track;
        this.setupAudioLevelDetection(peerId, event.streams[0]);
      }
      
      if (this.onRemoteStreamCallback) {
        this.onRemoteStreamCallback(peerId, event.streams[0], event.track.kind);
      }
    };

    // Handle track removal
    pc.onremovetrack = (event) => {
      console.log(`Track removed from ${peerId}:`, event.track.kind);
      if (this.onRemoteTrackRemovedCallback) {
        this.onRemoteTrackRemovedCallback(peerId, event.track.kind);
      }
    };

    // Handle connection state
    pc.onconnectionstatechange = () => {
      console.log(`Connection state for ${peerId}:`, pc.connectionState);
      
      if (pc.connectionState === 'failed') {
        console.log(`Connection failed for ${peerId}`);
        this.restartIce(peerId);
      }
    };

    return pc;
  }

  async restartIce(peerId) {
    const pc = this.peers.get(peerId);
    if (!pc) return;

    try {
      console.log(`Restarting ICE for ${peerId}`);
      const offer = await pc.createOffer({ iceRestart: true });
      await pc.setLocalDescription(offer);
      
      this.socket.emit('webrtc-offer', {
        to: peerId,
        offer: offer
      });
    } catch (err) {
      console.error('Error restarting ICE:', err);
    }
  }

  setupAudioLevelDetection(peerId, stream) {
    if (!this.activeSpeakerDetection) return;

    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = audioContext.createAnalyser();
      const source = audioContext.createMediaStreamSource(stream);
      
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8;
      source.connect(analyser);
      
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      
      const checkAudioLevel = () => {
        if (!this.peers.has(peerId)) {
          source.disconnect();
          audioContext.close();
          return;
        }
        
        analyser.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
        this.audioLevels.set(peerId, average);
        
        requestAnimationFrame(checkAudioLevel);
      };
      
      checkAudioLevel();
    } catch (err) {
      console.warn('Failed to setup audio level detection:', err);
    }
  }

  getActiveSpeaker() {
    let maxLevel = 0;
    let activeSpeaker = null;
    
    this.audioLevels.forEach((level, peerId) => {
      if (level > maxLevel) {
        maxLevel = level;
        activeSpeaker = peerId;
      }
    });
    
    // Only return if above threshold
    return maxLevel > 10 ? activeSpeaker : null;
  }

  async createOffer(peerId) {
    const pc = await this.createPeerConnection(peerId, true);
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    return offer;
  }

  async handleOffer(peerId, offer) {
    const pc = await this.createPeerConnection(peerId, false);
    await pc.setRemoteDescription(new RTCSessionDescription(offer));
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    return answer;
  }

  async handleAnswer(peerId, answer) {
    const pc = this.peers.get(peerId);
    if (pc) {
      await pc.setRemoteDescription(new RTCSessionDescription(answer));
    }
  }

  async handleIceCandidate(peerId, candidate) {
    const pc = this.peers.get(peerId);
    if (pc) {
      try {
        await pc.addIceCandidate(new RTCIceCandidate(candidate));
      } catch (err) {
        console.error('Error adding ICE candidate:', err);
      }
    }
  }

  updateLocalStream(newStream) {
    this.localStream = newStream;
    
    // Replace tracks in all peer connections
    this.peers.forEach((pc, peerId) => {
      const senders = pc.getSenders();
      
      newStream.getTracks().forEach(newTrack => {
        const sender = senders.find(s => s.track && s.track.kind === newTrack.kind);
        if (sender) {
          sender.replaceTrack(newTrack).catch(err => {
            console.error(`Error replacing ${newTrack.kind} track for ${peerId}:`, err);
          });
        } else {
          pc.addTrack(newTrack, newStream);
        }
      });
    });
  }

  async addTrack(track, stream) {
    // Add track to all peer connections
    this.peers.forEach((pc, peerId) => {
      try {
        pc.addTrack(track, stream);
      } catch (err) {
        console.error(`Error adding track to ${peerId}:`, err);
      }
    });
  }

  async removeTrack(track) {
    // Remove track from all peer connections
    this.peers.forEach((pc, peerId) => {
      try {
        const sender = pc.getSenders().find(s => s.track === track);
        if (sender) {
          pc.removeTrack(sender);
        }
      } catch (err) {
        console.error(`Error removing track from ${peerId}:`, err);
      }
    });
  }

  closePeerConnection(peerId) {
    const pc = this.peers.get(peerId);
    if (pc) {
      pc.close();
      this.peers.delete(peerId);
      this.remoteTracks.delete(peerId);
      this.audioLevels.delete(peerId);
    }
  }

  cleanup() {
    this.peers.forEach((pc, peerId) => {
      this.closePeerConnection(peerId);
    });
    
    this.peers.clear();
    this.remoteTracks.clear();
    this.audioLevels.clear();
  }

  getConnectionStats(peerId) {
    const pc = this.peers.get(peerId);
    if (!pc) return null;

    return pc.getStats().then(stats => {
      const result = {
        bitrate: { audio: 0, video: 0 },
        packetLoss: 0,
        rtt: 0,
        jitter: 0
      };

      stats.forEach(report => {
        if (report.type === 'inbound-rtp') {
          if (report.mediaType === 'video') {
            result.bitrate.video = report.bytesReceived * 8 / report.timestamp;
          } else if (report.mediaType === 'audio') {
            result.bitrate.audio = report.bytesReceived * 8 / report.timestamp;
          }
          result.packetLoss = report.packetsLost || 0;
          result.jitter = report.jitter || 0;
        } else if (report.type === 'candidate-pair' && report.state === 'succeeded') {
          result.rtt = report.currentRoundTripTime || 0;
        }
      });

      return result;
    });
  }
}

export default WebRTCService;
